import { type DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { type ColumnOrderState, type Updater, type ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useLocalStorage } from "react-use";
import { mergeColumnOrder } from "@/components/DataTable/utils";

export function useTableColumnOrder<TData>(tableId: string, columns: Array<ColumnDef<TData, any>>) {
  const defaultOrder = useMemo(() => columns.map((c) => c.id!), [columns]);
  const [storedOrder, setStoredOrder] = useLocalStorage(`table-order-${tableId}`, defaultOrder);

  const mergedColumns = useMemo(() => mergeColumnOrder(storedOrder, defaultOrder), [storedOrder, defaultOrder]);

  const onColumnOrderChange = (updater: Updater<ColumnOrderState>) => {
    const updatedColumnOrder = typeof updater === "function" ? updater(mergedColumns) : updater;
    setStoredOrder(updatedColumnOrder);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      onColumnOrderChange((prevColumnOrder) => {
        const oldIndex = prevColumnOrder.indexOf(active.id.toString());
        const newIndex = prevColumnOrder.indexOf(over.id.toString());

        // Safety check for invalid indices
        if (oldIndex === -1 || newIndex === -1) return prevColumnOrder;

        return arrayMove(prevColumnOrder, oldIndex, newIndex);
      });
    }
  };

  return {
    columnOrder: mergedColumns,
    onColumnOrderChange,
    handleDragEnd,
  };
}
